import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import Grid from '@material-ui/core/Grid';

import { Card } from 'core/components/card';
import { Label } from 'core/components/label';

import useStyles from './loss-history.styles';
import { LOSS_SUMMARY_WINDOWS, summarizeLossHistory } from './helpers';

const Stat = ({ label, value, classes }) => (
  <Grid item xs={4} className={classes.stat}>
    <Label type="infoLabel">{label}</Label>
    <Label type="infoValue">{value}</Label>
  </Grid>
);

Stat.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.node.isRequired,
  classes: PropTypes.object.isRequired
};

// #33939 — real-time rollup of the loss history (excludes Ignored entries). Recomputes on every
// render from the live Formik value, so it updates as entries are added, edited, ignored, deleted.
// Visible to both UW and agents; carries no Ignore control itself. Counts one loss EVENT per day
// and reports counts only — paid amounts are per-claim values shown on each entry, not summed here.
const LossSummary = ({ entries, referenceDate }) => {
  const classes = useStyles();
  const summary = useMemo(() => summarizeLossHistory(entries, referenceDate), [entries, referenceDate]);
  const causes = Object.entries(summary.byCause);

  return (
    <Card className={classes.summaryCard} type="secondary">
      <Label type="formTitle">Loss Summary</Label>
      <Grid container spacing={4} className={classes.summaryStats}>
        <Stat label="Total Losses" value={summary.totalLosses} classes={classes} />
        {LOSS_SUMMARY_WINDOWS.map((years) => (
          <Stat key={years} label={`Losses (last ${years} yrs)`} value={summary.frequency[years]} classes={classes} />
        ))}
      </Grid>
      <Label type="infoLabel" className={classes.causeHeader}>
        By Cause of Loss
      </Label>
      {causes.length ? (
        <div className={classes.causeList}>
          {causes.map(([cause, count]) => (
            <Label key={cause} type="infoValue" className={classes.causeItem}>
              {`${count}x ${cause}`}
            </Label>
          ))}
        </div>
      ) : (
        <Label type="infoValue">No losses recorded</Label>
      )}
    </Card>
  );
};

LossSummary.propTypes = {
  entries: PropTypes.array,
  referenceDate: PropTypes.string
};

LossSummary.defaultProps = {
  entries: [],
  referenceDate: null
};

export default LossSummary;
